import { useNavigate } from 'react-router-dom';
import { Loader2, MapPin, ShieldAlert, X } from 'lucide-react';
import { Skeleton } from '@shared/ui/feedback/Skeleton';
import { usePanicSos, PANIC_HOLD_MS, type Coords } from '@features/panic/usePanicSos';
import { WidgetCard } from './WidgetCard';
import { widgetTitle, type WidgetProps } from './registry';
import { MarqueeText } from '@shared/ui/primitives/MarqueeText';

/**
 * Home widget: a compact, working version of the Panic/SOS screen — shows any
 * active alert, a row of coloured hold-to-alert buttons (one per emergency
 * type) and the member's location status. "Open" jumps to the full panic page
 * (history, contacts, map).
 */
export function PanicWidget({ config }: WidgetProps): JSX.Element {
  const navigate = useNavigate();
  const panic = usePanicSos();

  return (
    <>
    {/* Non-tappable card: a tappable (role=button) card body would swallow
        the hold gesture on the emergency buttons. "Open" still navigates. */}
    <WidgetCard
      title={widgetTitle(config)}
      actionLabel="Open"
      onAction={() => navigate('/app/panic')}
    >
      {/* 1. Active alert */}
      {panic.activeLog ? (
        <div className="flex items-center gap-2.5 rounded-lg py-2 pl-3.5 pr-2 text-white" style={{ backgroundColor: 'var(--danger)' }}>
          <div className="min-w-0 flex-1">
            <MarqueeText as="p" className="text-body-sm font-semibold">Alert active</MarqueeText>
            <MarqueeText as="p" className="text-caption text-white/85">
              {`${statusLabel(panic.activeLog.status)} · estate security notified`}
            </MarqueeText>
          </div>
          <button
            type="button"
            onClick={() => panic.cancelActive(panic.activeLog!.panic_log_id)}
            disabled={panic.cancelling}
            className="flex min-h-9 shrink-0 items-center gap-1 rounded-md bg-card px-3 text-caption font-semibold text-danger disabled:opacity-60"
          >
            <X className="h-3.5 w-3.5" strokeWidth={2} />
            Cancel
          </button>
        </div>
      ) : null}

      {/* 2. Hold-to-alert buttons, one per emergency type, three to a row */}
      {panic.isLoading ? (
        <Skeleton className="h-16 rounded-lg" />
      ) : panic.tiles.length === 0 ? (
        <p className="text-body-sm text-ink-muted">No emergency types configured.</p>
      ) : (
        <div className="grid grid-cols-3 gap-2">
          {panic.tiles.map((tile) => {
            const holding = panic.holdingId === tile.id;
            const Icon = tile.icon;
            return (
              <button
                key={tile.id}
                type="button"
                disabled={panic.sending}
                aria-label={`${tile.title}: press and hold to alert`}
                style={{ backgroundColor: tile.color, touchAction: 'pan-x pan-y' }}
                onPointerDown={(e) => panic.startHold(e, tile, tile.id)}
                onContextMenu={(e) => e.preventDefault()}
                className="relative flex h-16 min-w-0 select-none flex-col items-center justify-center gap-0.5 overflow-hidden rounded-[1rem] px-2 text-white"
              >
                {/* Fill rises from the bottom over the hold duration. */}
                <span
                  aria-hidden
                  className="absolute inset-0 origin-bottom bg-white/25"
                  style={{
                    transform: holding ? 'scaleY(1)' : 'scaleY(0)',
                    transition: holding ? `transform ${PANIC_HOLD_MS}ms linear` : 'transform 150ms ease',
                  }}
                />
                <Icon className="relative h-[1.125rem] w-[1.125rem]" strokeWidth={2} />
                <MarqueeText className="relative max-w-full text-body-sm font-semibold">{tile.title}</MarqueeText>
              </button>
            );
          })}
        </div>
      )}

      {/* 3. Location, or the hold prompt while a button is held */}
      <p className="flex items-center justify-center gap-1 text-caption text-ink-secondary">
        <MapPin className="h-3.5 w-3.5 shrink-0" strokeWidth={2} />
        {panic.holdingId ? 'Keep holding…' : `${locationLine(panic.coords, panic.locating)} · hold to alert`}
      </p>
    </WidgetCard>

    {/* Full-screen sending mask — mirrors the panic page. Solid branded
        backdrop (the `primary-*` palette has no <alpha-value> channel, so an
        /opacity modifier would render transparent); inline rgba fallback keeps
        it opaque before branding vars resolve. */}
    {panic.sending ? (
      <div
        className="fixed inset-0 z-[70] flex flex-col items-center justify-center gap-5 bg-primary-900 text-white"
        style={{ backgroundColor: 'var(--primary-900, #0f172a)' }}
        role="status"
        aria-live="assertive"
      >
        <span className="relative flex h-24 w-24 items-center justify-center">
          <span className="absolute inline-flex h-full w-full animate-ping rounded-pill bg-white/30" />
          <span className="relative flex h-24 w-24 items-center justify-center rounded-pill bg-white/15">
            <ShieldAlert className="h-12 w-12 text-white" strokeWidth={2.2} />
          </span>
        </span>
        <div className="flex items-center gap-2.5">
          <Loader2 className="h-6 w-6 animate-spin text-white" />
          <span className="font-display text-display-lg">Sending…</span>
        </div>
        <span className="text-label text-white/80">Alerting your emergency contacts</span>
      </div>
    ) : null}
    </>
  );
}

function statusLabel(status: string): string {
  return status.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

/** Location status for the widget subtitle. */
export function locationLine(coords: Coords | null, locating: boolean): string {
  if (coords) {
    return coords.accuracy ? `Location shared · ±${Math.round(coords.accuracy)}m` : 'Location shared';
  }
  return locating ? 'Locating…' : 'Location unavailable';
}
